import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Helmet } from 'react-helmet';
import {
  Box,
  BoxProps,
  Container,
  Stack,
  StackProps,
  Typography,
  TypographyProps,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { TP_COLORS } from '../../../constants';
import {
  getUrlConvertedString,
  getSubjectColorNew,
  getSubjectIconName,
} from '../../../helper';
import { useAppSelector } from '../../../hooks';

import { getAuthState } from '../../../store/AuthSlice';
import {
  ICurriculumForHome,
  ISubjectForHome,
  getCurriculumState,
  getCurriculumsForHomePage,
} from '../../../store/CurriculumSlice';
import {
  getTopicState,
  getTopicsWithSubTopics,
} from '../../../store/TopicSlice';
import { IGroupedTopic } from '../../../store/SubTopicSlice';
import { getSubjectProgress } from '../../../store/ProgressSlice';

import HomeFooter from '../../Home/components/HomeFooter';
import FullScreenLoader from '../../../components/Loaders/FullScreenloader';
import Breadcrumbs, {
  BreadcrumbOption,
} from '../../../components/BreadCrumb/Breadcrumbs';
import SubjectProgressBar from '../Subject/components/SubjectProgressBar';

import SubTopicList from './components/SubTopicList';
import ExploreTopicsBar from '../Resources/components/ExploreTopicsBar';
import SubTopicSection from './components/SubTopicSection';
import { getTenantsState } from '../../../store/V2/TenantsSlice';
import { getTopicStructuredData } from '../../../helper/StructuredData';

const topStackProps: StackProps['sx'] = {
  direction: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  style: { padding: '30px 0' },
};

const subjectBoxProps: BoxProps = {
  display: 'flex',
  alignItems: 'center',
  sx: {
    justifyContent: { xs: 'space-between', sm: 'flex-start' },
  },
};

const subjectNameTypoProps: TypographyProps['sx'] = {
  component: 'h1',
  variant: 'h5',
  fontSize: '28px',
};

const subjectDescriptionTypoProps: TypographyProps = {
  fontSize: '14px',
  fontWeight: '550',
  marginTop: '12px',
  maxWidth: '800px',
};

const subjectTypoProps: TypographyProps = {
  fontSize: '18px',
  fontWeight: '550',
  marginTop: '5px',
};

const constructBreadcrumbOptions = (
  state: IState,
  topicData?: IGroupedTopic
) => {
  const options: Array<BreadcrumbOption> = [
    {
      type: 'link',
      title: 'Home',
      link: `${process.env.PUBLIC_URL}`,
    },
  ];

  let curriculum = '';

  if (state.selectedCurriculum) {
    curriculum = getUrlConvertedString(state.selectedCurriculum.name);

    options.push({
      type: 'link',
      title: state.selectedCurriculum.name,
      link: `${process.env.PUBLIC_URL}/${curriculum}`,
    });
  }

  if (state.selectedSubject && state.selectedCurriculum) {
    const subject = getUrlConvertedString(state.selectedSubject.name);
    const subjectLevel = getUrlConvertedString(
      state.selectedSubject.subjectLevel
    );
    const subjectCode = getUrlConvertedString(
      state.selectedSubject.subjectCode
    );

    options.push({
      type: 'link',
      title: state.selectedSubject.name,
      link: `${process.env.PUBLIC_URL}/dashboard/${curriculum}/${subject}/${subjectLevel}/${subjectCode}`,
    });
  }

  if (topicData) {
    options.push({
      type: 'text',
      title: topicData.name,
      highlight: true,
    });
  }

  return options;
};

interface IState {
  selectedCurriculum: ICurriculumForHome | undefined;
  selectedSubject: ISubjectForHome | undefined;
}

const initialState: IState = {
  selectedCurriculum: undefined,
  selectedSubject: undefined,
};

const TopicPage: React.FC = () => {
  const params = useParams();
  const dispatch = useDispatch();
  const theme = useTheme();
  const belowMd = useMediaQuery(theme.breakpoints.down('md'));

  // Store
  const authState = useAppSelector(getAuthState);
  const topicState = useAppSelector(getTopicState);
  const curriculumState = useAppSelector(getCurriculumState);
  const tenantState = useAppSelector(getTenantsState);

  const { curriculumsForHomePage } = curriculumState;
  const { topicsWithSubTopics, status: topicStatus } = topicState;
  const userRole = authState.user?.role.name;
  const { appTenant } = tenantState;

  // State
  const [state, setState] = useState<IState>(initialState);
  const [selectedTopic, setSelectedTopic] = useState<IGroupedTopic>();
  const [topicList, setTopicList] = useState<Array<IGroupedTopic>>([]);

  const subjectColor = getSubjectColorNew(state.selectedSubject?.name || '');
  const breadcrumbOptions = constructBreadcrumbOptions(state, selectedTopic);
  const selectedTopicIndex = topicList.length
    ? topicList.findIndex((item) => item._id === params.topicId)
    : -1;

  const structuredData = getTopicStructuredData({
    curriculum: state.selectedCurriculum?.name || '',
    subject: state.selectedSubject?.name || '',
    subjectLevel: state.selectedSubject?.subjectLevel || '',
    subjectCode: state.selectedSubject?.subjectCode || '',
    topic: selectedTopic?.name || '',
    appTenant,
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (curriculumsForHomePage.length === 0) {
      dispatch(getCurriculumsForHomePage());
    }
  }, []);

  useEffect(() => {
    if (curriculumsForHomePage.length) {
      let selectedCurriculum = curriculumsForHomePage.find(
        (curriculumItem) =>
          getUrlConvertedString(curriculumItem.name) === params.curriculum
      );
      if (!selectedCurriculum) {
        selectedCurriculum = { ...curriculumsForHomePage[0] };
      }

      if (selectedCurriculum.subjects.length) {
        let selectedSubject = selectedCurriculum.subjects.find(
          (subjectItem) =>
            getUrlConvertedString(subjectItem.name) === params.subject &&
            getUrlConvertedString(subjectItem.subjectLevel) ===
              params.subjectLevel &&
            getUrlConvertedString(subjectItem.subjectCode) ===
              params.subjectCode
        );

        if (!selectedSubject) {
          selectedSubject = { ...selectedCurriculum.subjects[0] };
        }

        setState({
          selectedCurriculum,
          selectedSubject,
        });
      }
    }
  }, [params]);

  useEffect(() => {
    if (
      topicStatus !== 'loading' &&
      state.selectedCurriculum &&
      state.selectedSubject &&
      checkTopicsFetched()
    ) {
      dispatch(
        getTopicsWithSubTopics({
          curriculum: state.selectedCurriculum.name,
          subject: state.selectedSubject.name,
          subjectCode: state.selectedSubject.subjectCode,
          subjectLevel: state.selectedSubject.subjectLevel,
        })
      );
    }

    if (
      userRole === 'Student' &&
      state.selectedCurriculum &&
      state.selectedSubject
    ) {
      dispatch(
        getSubjectProgress({
          curriculum: state.selectedCurriculum.name,
          subject: state.selectedSubject.name,
          subjectLevel: state.selectedSubject.subjectLevel,
        })
      );
    }
  }, [state.selectedSubject]);

  useEffect(() => {
    if (topicsWithSubTopics.length) {
      const topics = topicsWithSubTopics.filter((item) => !item.isAssessment);

      if (topics.length) {
        setTopicList(topics);

        setSelectedTopic(
          topics.find(
            (topicItem) =>
              getUrlConvertedString(topicItem._id) === params.topicId
          )
        );
      }
    }
  }, [topicsWithSubTopics, params.topicId]);

  const checkTopicsFetched = () => {
    if (
      state.selectedSubject &&
      topicsWithSubTopics.length &&
      topicsWithSubTopics[0].subject === state.selectedSubject.name &&
      topicsWithSubTopics[0].subjectLevel ===
        state.selectedSubject.subjectLevel &&
      topicsWithSubTopics[0].subjectCode === state.selectedSubject.subjectCode
    ) {
      return false;
    }
    return true;
  };

  if (!selectedTopic) {
    return <FullScreenLoader />;
  }

  return (
    <Box sx={{ backgroundColor: '#E5F7F666' }}>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>
      <div style={{ minHeight: '80vh' }}>
        <Container maxWidth="lg" sx={{ mb: '60px', pt: '10px' }}>
          <Stack {...topStackProps}>
            <Breadcrumbs
              options={breadcrumbOptions}
              showDropDown={belowMd}
              textColor={TP_COLORS.gray.light}
              highlightColor={subjectColor}
            />
            {userRole === 'Student' && <SubjectProgressBar />}
          </Stack>

          <Box
            display="flex"
            sx={{
              flexDirection: { xs: 'column', md: 'row' },
              position: 'relative',
            }}
          >
            {state.selectedCurriculum && (
              <Box
                component="aside"
                sx={{
                  position: 'sticky',
                  top: !belowMd ? '80px' : null,
                  height: 'fit-content',
                  alignSelf: 'flex-start',
                  zIndex: 2,
                  minWidth: belowMd ? '100%' : '25%',
                  maxWidth: belowMd ? '100%' : '25%',
                }}
              >
                <SubTopicSection
                  selectedTopic={selectedTopic}
                  selectedTopicIndex={selectedTopicIndex}
                  topicList={topicList}
                  subjectColor={subjectColor}
                />
              </Box>
            )}
            <Stack width="100%">
              <Box
                component="section"
                sx={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  color: TP_COLORS.white,
                  borderRadius: '15px',
                  mb: '15px',
                  background: `url('${process.env.PUBLIC_URL}/images/subjectPage/shape-header.png') top right/contain no-repeat, ${subjectColor}`,
                  overflow: 'hidden',
                }}
              >
                <Box
                  padding="24px"
                  sx={{
                    zIndex: 2,
                    position: 'relative',
                    marginRight: '-5%',
                  }}
                >
                  <Box {...subjectBoxProps}>
                    <Typography {...subjectNameTypoProps}>
                      {selectedTopic.name || ''} Revision Resources
                    </Typography>
                  </Box>
                  <Typography {...subjectTypoProps}>
                    {state.selectedCurriculum?.name || ' '}{' '}
                    {state.selectedSubject?.name || ''}
                  </Typography>
                  <Typography {...subjectDescriptionTypoProps}>
                    Explore our{' '}
                    <strong>
                      {state.selectedCurriculum?.name || ' '}{' '}
                      {selectedTopic.name || ''}
                    </strong>{' '}
                    course with <strong>interactive quizzes</strong>,{' '}
                    <strong>downloadable worksheets</strong>,{' '}
                    <strong>instructional videos</strong>, and{' '}
                    <strong>AI-marked questions</strong>. Designed to help
                    students{' '}
                    <strong>master {selectedTopic.name || ''} topics</strong>{' '}
                    from the {state.selectedCurriculum?.name || ' '} syllabus,
                    our resources provide clear explanations and plenty of
                    practice.
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: { xs: 'none', sm: 'flex' },
                    zIndex: 1,
                  }}
                >
                  <Stack justifyContent="center">
                    <img
                      src={`${
                        process.env.PUBLIC_URL
                      }/images/landingPage/icon-${getSubjectIconName(
                        state.selectedSubject?.name || ''
                      )}-white.avif`}
                      alt="subject-img"
                      style={{ height: '120px' }}
                    />
                  </Stack>
                </Box>
              </Box>
              {selectedTopic &&
              selectedTopic.subTopics &&
              selectedTopic.subTopics.length ? (
                <SubTopicList selectedTopic={selectedTopic} />
              ) : null}

              {selectedTopic && selectedTopic.name === 'Overview' && (
                <ExploreTopicsBar topicData={selectedTopic} />
              )}
            </Stack>
          </Box>
        </Container>
      </div>

      <HomeFooter />
    </Box>
  );
};

export default TopicPage;
